import type { MaybeRef, Ref, ShallowRef } from 'vue';
import { computed, onMounted, ref, shallowRef, unref, watch } from 'vue';

export interface UseAsyncDataOptions<TResult> {
  /**
   * @description A boolean Ref or const to enable or disable the query.
   * @default true
   */
  enabled?: MaybeRef<boolean>;
  /**
   * @description The initial data to be set to the result. Will be replaced by the real result
   * @default undefined
   */
  initData?: TResult;
  /**
   * @description Whether or not to keep previous result when the query is fetch again (for example when a variable changes). This can be useful to prevent a flash of empty content.
   * @default false
   */
  keepPreviousResult?: boolean;
}

export interface FetchMoreArgs<TResult, TArgs> {
  /**
   * @description New variables to be used in the query. For example different offset for pagination
   */
  args?: Partial<TArgs>;
  /**
   * @description A function to update the previous result with the new data.
   * @param previousResult The previous result
   * @param data The new data
   * @returns The updated value that will be set to the result
   */
  update?: (previousResult: TResult, data: TResult) => TResult;
}

export interface UseAsyncDataReturn<TResult, TArgs, TError = any> {
  /**
   * @description True if the promise is in progress.
   */
  loading: Ref<boolean, boolean>;
  /**
   * @description Execute a variant of the query to retrieve additional data to be merged with the existing one. Useful for pagination.
   */
  fetchMore: (fetchMoreArgs: FetchMoreArgs<TResult, TArgs>) => Promise<void>;
  /**
   * @description  Execute the promise, optionally with new variables.
   */
  execute: (refetchArgs?: TArgs) => Promise<TResult>;
  /**
   * @description result data object
   */
  result: ShallowRef<TResult>;
  /**
   * @description True if fetching using the fetchMore handler.
   */
  fetchingMore: Ref<boolean, boolean>;
  /**
   * @description  Event hook called when a new result is available. Handler is called with: `result`.
   */
  onResult: (handler: (data: TResult) => void) => void;
  /**
   * @description   Event hook called when an error occurs.Handler is called with: `error`
   */
  onError: (handler: (error: TError) => void) => void;
  /**
   * @description  The error if the promise fails.
   */
  error: Ref<TError>;
  /**
   * @description Reset the state of the query. Sets data, errors and loading to their initial state.
   */
  reset: () => void;
}

export const useAsyncData = <TResult, TArgs, TError = any>(
  dataFn: (args: TArgs) => Promise<TResult>,
  args: MaybeRef<TArgs>,
  options: UseAsyncDataOptions<TResult> = {
    keepPreviousResult: false,
    enabled: true,
    initData: undefined,
  }
): UseAsyncDataReturn<TResult, TArgs> => {
  const loading = ref(false);
  const fetchingMore = ref(false);
  const error = ref<TError>(null);
  const result = shallowRef<TResult>(options.initData);
  const enabled = computed(() => unref(options.enabled) ?? true);
  let onResultHandler: (data: TResult) => void;
  let onErrorHandler: (error: TError) => void;

  const setResultHandler = (handler: (data: TResult) => void) => {
    onResultHandler = handler;
  };
  const setErrorHandler = (handler: (error: TError) => void) => {
    onErrorHandler = handler;
  };

  const onResult = (data: TResult) => {
    result.value = data;
    if (onResultHandler) {
      onResultHandler(data);
    }
  };

  const onError = (e: TError) => {
    error.value = e;
    if (onErrorHandler) {
      onErrorHandler(e);
    }
  };

  const fetchMore = async ({
    args: fetchMoreArgs,
    update,
  }: {
    args?: Partial<TArgs>;
    update?: (previousResult: TResult, data: TResult) => TResult;
  }) => {
    error.value = null;
    fetchingMore.value = true;
    try {
      const data = await dataFn({ ...unref(args), ...fetchMoreArgs });
      if (update) {
        onResult(update(result.value, data));
      }
    } catch (e) {
      onError(e);
    }
    fetchingMore.value = false;
  };

  const execute = async (refetchArgs?: TArgs) => {
    if (!options.keepPreviousResult) {
      result.value = options.initData;
    }
    error.value = null;
    loading.value = true;
    try {
      const data = await dataFn(refetchArgs || unref(args));
      onResult(data);
      return data;
    } catch (e) {
      onError(e);
    } finally {
      loading.value = false;
    }
  };

  const autoFetch = () => {
    if (enabled.value) {
      execute();
    }
  };

  const reset = () => {
    loading.value = false;
    fetchingMore.value = false;
    error.value = null;
    result.value = options.initData;
  };

  watch([() => unref(args), enabled], autoFetch, { deep: true });

  onMounted(autoFetch);

  return {
    loading,
    fetchMore,
    execute,
    result,
    fetchingMore,
    onResult: setResultHandler,
    onError: setErrorHandler,
    error,
    reset,
  };
};
